import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonBase,
  IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DateRangePickerInline = ({
  dateRange,
  setDateRange,
  handleApply,
  onClose,
  handleClearFilter
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);


  // Initialize startDate and endDate from dateRange prop (milliseconds)
  useEffect(() => {
    if (dateRange && dateRange.length === 2) {
      // Handle array of epoch timestamps
      const [startEpoch, endEpoch] = dateRange;
      setStartDate(new Date(startEpoch * 1000));
      setEndDate(new Date(endEpoch * 1000));
      setCurrentMonth(new Date(startEpoch * 1000));
    } else if (dateRange && dateRange.length === 1) {
      // Handle object format (existing code)
      const { startDate: s, endDate: e } = dateRange[0];
      setStartDate(s ? new Date(s) : null);
      setEndDate(e ? new Date(e) : null);
      setCurrentMonth(s ? new Date(s) : new Date());
    } else {
      setStartDate(null);
      setEndDate(null);
    }
    setIsSelectingEnd(false);
    setHoverDate(null);
  }, [dateRange]);

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate) || date < startDate) {
      setStartDate(date);
      setEndDate(null);
      setIsSelectingEnd(true);
    } else if (startDate && !endDate) {
      setEndDate(date);
      setIsSelectingEnd(false);
    }
  };

  const handleDateHover = (date) => {
    if (isSelectingEnd && startDate && !endDate) {
      setHoverDate(date);
    }
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setDateRange([]);
    setIsSelectingEnd(false);
    setHoverDate(null);
    handleClearFilter(); // Call the parent's clear function
    onClose();
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Empty slots before the first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!date) return false;

    const checkStart = startDate;
    const checkEnd = endDate || hoverDate;

    if (checkStart && checkEnd) {
      return date > checkStart && date < checkEnd;
    }
    return false;
  };

  const isDateRangeEndpoint = (date) => {
    if (!date) return false;
    return (
      (startDate && date.getTime() === startDate.getTime()) ||
      (endDate && date.getTime() === endDate.getTime())
    );
  };

  const isDateDisabled = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    return date > today; // Disable future dates
  };

  const monthYear = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentMonth);

  return (
    <Paper sx={{ width: 320, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      {/* Month navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          px: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigateMonth(-1)}
          sx={{
            color: 'primary.main',
            '&:hover': { backgroundColor: 'primary.light' },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '1rem',
          }}
        >
          {monthYear}
        </Typography>
        <IconButton
          size="small"
          onClick={() => navigateMonth(1)}
          sx={{
            color: 'primary.main',
            '&:hover': { backgroundColor: 'primary.light' },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Days of week header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          mb: 1,
        }}
      >
        {weekdays.map((day) => (
          <Typography
            key={day}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '16px',
              lineHeight: 1.75,
              py: 0.5,
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          mb: 2,
        }}
      >
        {days.map((day, index) => (
          <Box
            key={index}
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}
          >
            {day && (
              <ButtonBase
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => handleDateHover(day)}
                disabled={isDateDisabled(day)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  transition: 'all 0.2s ease',
                  color: isDateRangeEndpoint(day)
                    ? 'primary.contrastText'
                    : 'text.primary',
                  backgroundColor: isDateRangeEndpoint(day)
                    ? 'primary.main'
                    : isDateInRange(day)
                      ? 'rgba(244, 67, 54, 0.12)'
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: isDateRangeEndpoint(day)
                      ? 'primary.dark'
                      : isDateInRange(day)
                        ? 'rgba(244, 67, 54, 0.2)'
                        : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    color: 'text.disabled',
                    cursor: 'not-allowed',
                    opacity: 0.5,
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                }}
              >
                {day.getDate()}
              </ButtonBase>
            )}
          </Box>
        ))}
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={handleClear}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleApply(startDate, endDate)}
          disabled={!startDate || !endDate}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 1,
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Paper>
  );
};

export default DateRangePickerInline;
